import { Injectable, computed, signal } from '@angular/core';
import type { ProductType } from '../../models/rate-sheet.models';
import { DIM_TAB_APPLICABILITY, isApplicable } from './dim-tab-applicability';
import type { DimKey } from './dim-tab-applicability';

export const PIN_STORAGE_KEY = 'bn.rail.pins';

const VALID_DIMS = new Set<string>(Object.keys(DIM_TAB_APPLICABILITY));

function loadFromStorage(): DimKey[] {
  try {
    const raw = localStorage.getItem(PIN_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((k): k is DimKey => typeof k === 'string' && VALID_DIMS.has(k));
  } catch {
    return [];
  }
}

function persistToStorage(dims: readonly DimKey[]): void {
  try {
    localStorage.setItem(PIN_STORAGE_KEY, JSON.stringify(dims));
  } catch {
    /* quota exceeded or storage disabled — pins persist for session only */
  }
}

/**
 * Phase 10.1 — global signal-based store of pinned filter dimensions.
 * Pins are layout-only (which forms appear in the rail's ★ Pinned
 * section); filter VALUES live in RatesFilterStateService per ProductType.
 */
@Injectable({ providedIn: 'root' })
export class RailPinService {
  private readonly _pinned = signal<readonly DimKey[]>(loadFromStorage());

  readonly pinnedDims = computed<readonly DimKey[]>(() => this._pinned());

  isPinned(dim: DimKey): boolean {
    return this._pinned().includes(dim);
  }

  pin(dim: DimKey): void {
    const cur = this._pinned();
    if (cur.includes(dim)) return;
    const next = [...cur, dim];
    this._pinned.set(next);
    persistToStorage(next);
  }

  unpin(dim: DimKey): void {
    const cur = this._pinned();
    if (!cur.includes(dim)) return;
    const next = cur.filter(k => k !== dim);
    this._pinned.set(next);
    persistToStorage(next);
  }

  applicableForTab(tab: ProductType): DimKey[] {
    return this._pinned().filter(k => isApplicable(k, tab));
  }
}
